/**
 * 事件目录：唯一白名单。对应 tech/108 §8.2 / docs/111/003。
 *
 * `type` 不在这里声明，`telemetry-client` 一律拒绝（不出网、不进本地队列）。
 * L1 判断点事件（本次列在 JUDGMENT_EVENT_TYPES）payload 必须带 `correct: boolean | null` 字段——
 * 这是直接回应"无论对错都要采集"的工程约束，由 telemetry-client 在写入前做运行时校验。
 */

export type TelemetryLevel = 1 | 2 | 3;

export interface EventCatalogEntry {
  type: string;
  level: TelemetryLevel;
  /** true = 不等批量窗口，立即直发。 */
  critical?: boolean;
  /** 是否为"判断点"事件：payload 必须显式带 correct 字段。 */
  judgmentPoint?: boolean;
  description: string;
}

const L1_EVENTS: EventCatalogEntry[] = [
  { type: "experiment.started", level: 1, critical: true, description: "实验首次开始" },
  { type: "experiment.completed", level: 1, critical: true, description: "全部 Stage 完成" },
  { type: "experiment.abandoned", level: 1, description: "超时未活动 / 主动退出" },
  { type: "stage.started", level: 1, description: "进入某个子实验" },
  { type: "stage.completed", level: 1, description: "某个子实验完成" },
  { type: "step.started", level: 1, description: "Mission 激活新步骤" },
  { type: "step.completed", level: 1, description: "步骤判定通过" },
  { type: "step.blocked", level: 1, description: "步骤门控未满足（act 未操作 / record 未记录）" },
  { type: "guess.submitted", level: 1, critical: true, judgmentPoint: true, description: "① 猜想提交" },
  { type: "variable.selected", level: 1, critical: true, judgmentPoint: true, description: "② 控制变量选择提交" },
  { type: "equipment.selected", level: 1, description: "从操作区选取器材/参数" },
  { type: "relation.created", level: 1, description: "装配/接触关系变更（如木块放到某接触面上）" },
  { type: "adjustment.changed", level: 1, description: "连续/离散参数调节（加减砝码等）" },
  { type: "measurement.read", level: 1, description: "④ 仪表读数（P0 由系统按力学模型直接给出，无人工读数确认，故非判断点）" },
  { type: "data.recorded", level: 1, description: "④ 数据写入数据表" },
  { type: "pattern.selected", level: 1, critical: true, judgmentPoint: true, description: "⑤ “你发现了什么”选择题" },
  { type: "conclusion.submitted", level: 1, critical: true, judgmentPoint: true, description: "⑧ 结论提交" },
  { type: "exam.answer.submitted", level: 1, critical: true, judgmentPoint: true, description: "⑦ 中考题作答" },
  { type: "knowledge.shown", level: 1, description: "知识卡触发" },
  { type: "hint.requested", level: 1, description: "学生主动求助" },
  { type: "hint.viewed", level: 1, description: "系统给出提示" },
  { type: "error.triggered", level: 1, description: "命中错误规则（操作/读数/知识）" },
  { type: "error.recovered", level: 1, critical: true, description: "学生自主修正" },
  { type: "report.generated", level: 1, critical: true, description: "生成实验报告" },
];

const L2_EVENTS: EventCatalogEntry[] = [
  { type: "engine.exception", level: 2, description: "引擎异常兜底捕获" },
  { type: "pack.validate.failed", level: 2, description: "ExperimentPack 校验失败" },
  { type: "client.info", level: 2, description: "会话开始一次的客户端信息" },
  { type: "perf.step.slow", level: 2, description: "单步耗时超阈值" },
];

export const EVENT_CATALOG: Readonly<Record<string, EventCatalogEntry>> = Object.freeze(
  Object.fromEntries([...L1_EVENTS, ...L2_EVENTS].map((entry) => [entry.type, entry])),
);

export function getCatalogEntry(type: string): EventCatalogEntry | undefined {
  return EVENT_CATALOG[type];
}

export function isJudgmentPoint(type: string): boolean {
  return Boolean(EVENT_CATALOG[type]?.judgmentPoint);
}
